import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { NotificationBell } from "./NotificationBell";
import { GlobalSearch } from "./GlobalSearch";
import { emailQueueAPI } from "../lib/api";
import {
  LayoutDashboard,
  FileText,
  Settings,
  Users,
  FolderOpen,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  History,
  Upload,
  Inbox,
  Mail,
  Send,
  Database,
  UserCircle,
  MapPin,
  UserPlus,
  Key,
  ChevronDown,
  Cloud,
} from "lucide-react";

const LOGO_SPLENDOR = "https://customer-assets.emergentagent.com/job_protocol-hub-13/artifacts/6oqjboke_Splendor%20Logo%202020.jpg";

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/", roles: ["admin", "operatore"] },
  { icon: FileText, label: "Registro Protocollo", path: "/protocols", roles: ["admin", "operatore"] },
  { icon: Inbox, label: "Email", path: "/email-queue", roles: ["admin", "operatore"] },
  { icon: UserCircle, label: "Anagrafica", path: "/settings/contacts", roles: ["admin", "operatore"] },
  { icon: Users, label: "Utenti", path: "/settings/users", roles: ["admin"] },
  { icon: Mail, label: "Account Email", path: "/settings/email-accounts", roles: ["admin"] },
  { icon: FolderOpen, label: "Tipologie", path: "/settings/types", roles: ["admin"] },
  { icon: MapPin, label: "Ubicazioni", path: "/settings/locations", roles: ["admin", "operatore"] },
  { icon: Upload, label: "Import", path: "/settings/import", roles: ["admin"] },
  { icon: Database, label: "Backup", path: "/settings/backup", roles: ["admin"] },
  { icon: Cloud, label: "OneDrive", path: "/settings/onedrive", roles: ["admin"] },
  { icon: History, label: "Audit Log", path: "/settings/audit", roles: ["admin"] },
];

export function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: "", new: "", confirm: "" });
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [savingPassword, setSavingPassword] = useState(false);
  const [pendingEmailCount, setPendingEmailCount] = useState(0);

  // Fetch pending email count
  useEffect(() => {
    const fetchPending = async () => {
      try {
        const res = await emailQueueAPI.getAll({ status: "pending" });
        setPendingEmailCount(res.data?.pending || 0);
      } catch {}
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new !== passwordData.confirm) {
      setPasswordMessage({ text: "Le password non coincidono", type: "error" });
      return;
    }
    if (passwordData.new.length < 6) {
      setPasswordMessage({ text: "La password deve avere almeno 6 caratteri", type: "error" });
      return;
    }
    setSavingPassword(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordData.current,
          new_password: passwordData.new
        })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Errore");
      }
      setPasswordMessage({ text: "Password cambiata con successo", type: "success" });
      setTimeout(() => {
        setShowChangePassword(false);
        setPasswordData({ current: "", new: "", confirm: "" });
        setPasswordMessage(null);
      }, 1500);
    } catch (error) {
      setPasswordMessage({ text: error.message || "Errore durante il cambio password", type: "error" });
    } finally {
      setSavingPassword(false);
    }
  };

  const filteredNavItems = navItems.filter(
    (item) => item.roles.includes(user?.role)
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-20 bg-card border-b border-border z-50 flex items-center justify-between px-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center">
            <img src={LOGO_SPLENDOR} alt="Splendor Sud" className="h-10 object-contain" />
            <span className="text-xs font-medium text-[#2D7BB7]">Registro Protocollo</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/20 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          "lg:hidden fixed top-20 left-0 bottom-0 w-64 bg-card border-r border-border z-40 transform transition-transform duration-300 shadow-lg",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="px-4 pt-4 pb-1">
          <GlobalSearch collapsed={false} />
        </div>
        <nav className="p-4 space-y-2">
          {filteredNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                location.pathname === item.path
                  ? "bg-[#2D7BB7] text-white shadow-md"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <item.icon size={20} strokeWidth={1.5} />
              <span className="font-medium flex-1">{item.label}</span>
              {item.label === "Email" && pendingEmailCount > 0 && (
                <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center" data-testid="email-badge-mobile">
                  {pendingEmailCount}
                </span>
              )}
            </Link>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#2D7BB7] flex items-center justify-center text-white font-bold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={20} className="mr-2" />
            Esci
          </Button>
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "hidden lg:flex fixed top-0 left-0 bottom-0 flex-col bg-card border-r border-border z-30 transition-all duration-300 shadow-sm",
          sidebarOpen ? "w-64" : "w-20"
        )}
      >
        {/* Logo */}
        <div className="h-20 flex items-center justify-center px-4 border-b border-border">
          <div className="flex flex-col items-center">
            <img 
              src={LOGO_SPLENDOR} 
              alt="Splendor Sud" 
              className={cn(
                "object-contain transition-all duration-300",
                sidebarOpen ? "h-12" : "h-8"
              )} 
            />
            {sidebarOpen && (
              <span className="text-xs font-medium text-[#2D7BB7] mt-1">Registro Protocollo</span>
            )}
          </div>
        </div>
        {/* Toggle button */}
        <div className="absolute top-20 right-0 translate-x-1/2 z-10">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="rounded-full w-6 h-6 bg-card border-border shadow-sm"
            data-testid="sidebar-toggle"
          >
            <ChevronLeft
              size={20}
              className={cn("transition-transform", !sidebarOpen && "rotate-180")}
            />
          </Button>
        </div>

        {/* Search */}
        <div className="px-4 pt-3 pb-1">
          <GlobalSearch collapsed={!sidebarOpen} />
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {filteredNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-desktop-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                location.pathname === item.path
                  ? "bg-[#2D7BB7] text-white shadow-md"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
                !sidebarOpen && "justify-center px-0"
              )}
              title={!sidebarOpen ? item.label : undefined}
            >
              <div className="relative flex-shrink-0">
                <item.icon size={20} strokeWidth={1.5} />
                {!sidebarOpen && item.label === "Email" && pendingEmailCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="email-badge-collapsed">
                    {pendingEmailCount}
                  </span>
                )}
              </div>
              {sidebarOpen && (
                <>
                  <span className="font-medium flex-1">{item.label}</span>
                  {item.label === "Email" && pendingEmailCount > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center" data-testid="email-badge-desktop">
                      {pendingEmailCount}
                    </span>
                  )}
                </>
              )}
            </Link>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-border">
        </div>
      </aside>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div 
            className="fixed inset-0 bg-black/50" 
            onClick={() => setShowChangePassword(false)}
          />
          <div className="relative z-50 bg-card border border-border rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-heading font-bold text-foreground mb-4 flex items-center gap-2">
                <Key size={20} className="text-[#2D7BB7]" />
                Cambia Password
              </h2>
              
              {passwordMessage && (
                <div className={cn(
                  "mb-4 px-4 py-2 rounded-lg text-sm",
                  passwordMessage.type === "error" 
                    ? "bg-red-100 text-red-700" 
                    : "bg-emerald-100 text-emerald-700"
                )}>
                  {passwordMessage.text}
                </div>
              )}

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Password attuale
                  </label>
                  <input
                    type="password"
                    value={passwordData.current}
                    onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-foreground"
                    required
                    data-testid="current-password-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Nuova password
                  </label>
                  <input
                    type="password"
                    value={passwordData.new}
                    onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-foreground"
                    required
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Conferma nuova password
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirm}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-foreground"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowChangePassword(false);
                      setPasswordData({ current: "", new: "", confirm: "" });
                      setPasswordMessage(null);
                    }}
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    disabled={savingPassword}
                    className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
                  >
                    {savingPassword ? "Salvataggio..." : "Salva"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main
        className={cn(
          "min-h-screen pt-20 lg:pt-0 transition-all duration-300",
          sidebarOpen ? "lg:ml-64" : "lg:ml-20"
        )}
      >
        {/* Top bar with user info - always visible */}
        <div className="hidden lg:flex items-center justify-end gap-3 px-6 py-3 border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-20">
          <NotificationBell />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#2D7BB7] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-foreground leading-tight">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize leading-tight">{user?.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 ml-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowChangePassword(true);
                setUserMenuOpen(false);
              }}
              className="text-muted-foreground hover:text-foreground"
              data-testid="topbar-change-password-btn"
              title="Cambia Password"
            >
              <Key size={16} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              data-testid="topbar-logout-btn"
              title="Esci"
            >
              <LogOut size={16} />
            </Button>
          </div>
        </div>
        <div className="p-4 md:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
